import { ActionIcon, Box, Group, Paper, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconClock, IconMail, IconMailOpened, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { ReactNode } from 'react';
import { useClient } from 'src/clients/hooks/useClient';
import { useNotificationActions } from 'src/notifications/hooks/useNotificationActions';
import type { Notification } from 'src/notifications/types/Notification';
import { formatShortTimeAgo } from 'src/utils/dateFormatting';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import classes from './NotificationCardShell.module.css';
import NotificationDetailRow from './NotificationDetailRow';
import SnoozeModal from './SnoozeModal';

dayjs.extend(relativeTime);

export default function NotificationCardShell({
  notification,
  title,
  body,
  clientId,
  details,
  actions,
}: {
  notification: Notification;
  title: string;
  body: string;
  clientId?: string;
  details?: ReactNode;
  actions?: ReactNode;
}) {
  const { markRead, markUnread, dismiss } = useNotificationActions();
  const [expanded, { toggle }] = useDisclosure(false);
  const [snoozeOpened, { open: openSnooze, close: closeSnooze }] = useDisclosure(false);

  const { client } = useClient(clientId);
  const clientName = client
    ? formatName(client.firstName, client.lastName, NameFormat.NATURAL)
    : undefined;

  const isRead = notification.status === 'read';
  const receivedAgo = dayjs(notification.createdAt).fromNow();
  const summary = clientName ?? body;

  const handleToggle = () => {
    if (!expanded && !isRead) markRead.mutate(notification.id);
    toggle();
  };

  const toggleRead = () =>
    isRead ? markUnread.mutate(notification.id) : markRead.mutate(notification.id);

  return (
    <Paper
      variant="card"
      className={classes.card}
      bg={isRead ? undefined : 'brand.0'}
      py="xs"
      data-expanded={expanded}
      onClick={handleToggle}
    >
      <Stack gap={2}>
        <Group wrap="nowrap" gap="xs" align="center">
          <Text
            size="sm"
            fw={700}
            c={isRead ? 'gray.7' : 'gray.10'}
            lineClamp={1}
            style={{ flex: 1, minWidth: 0 }}
          >
            {title}
          </Text>
          <Box pos="relative" miw={92} h={22} style={{ flexShrink: 0 }}>
            <Group className={classes.meta} gap="xs" wrap="nowrap">
              <Text size="xs" c="gray.5" style={{ whiteSpace: 'nowrap' }}>
                {formatShortTimeAgo(notification.createdAt)}
              </Text>
              {!isRead && <Box className={classes.unreadDot} />}
            </Group>
            <Group
              className={classes.actions}
              gap={2}
              wrap="nowrap"
              onClick={event => event.stopPropagation()}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label={isRead ? 'Mark as unread' : 'Mark as read'}
                onClick={toggleRead}
              >
                {isRead ? <IconMail size={16} /> : <IconMailOpened size={16} />}
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Snooze"
                onClick={openSnooze}
              >
                <IconClock size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Dismiss"
                onClick={() => dismiss.mutate(notification.id)}
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          </Box>
        </Group>

        <Text size="xs" c="gray.6" lineClamp={1}>
          {summary}
        </Text>

        {expanded && (
          <Stack gap="xs" mt="xxs">
            <Stack gap={4}>
              {clientName && <NotificationDetailRow label="Client" value={clientName} />}
              {details}
              <NotificationDetailRow label="Received" value={receivedAgo} />
            </Stack>
            {actions && (
              <Group gap="xs" wrap="wrap">
                {actions}
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      <SnoozeModal opened={snoozeOpened} onClose={closeSnooze} notificationId={notification.id} />
    </Paper>
  );
}
